import * as React from 'react'
import {ContextValue} from '@datadog/browser-core'
import {datadogLogs, StatusType} from '@datadog/browser-logs'
import {TracingOption} from '@datadog/browser-rum-core/cjs/domain/tracing/tracer.types'
import {datadogRum} from '@datadog/browser-rum-slim'
import {mapObjIndexed} from 'ramda'

import {runInIdleTime} from '../../../_web/utils/performance/idleUntilUrgent'
import {experimentVariationsStore} from '../../../experiments/components/OpticksProvider'

type InitializeDataDogClientParametersType = {
  /** Current environment the website is running */
  appEnv?: string
  /** Current version of the website running */
  appVersion?: string
  /** Token used to send logs from your application into DD. You can configure tokens on “Client Tokens” in DD’s interface. */
  clientToken?: string | null
  /** RUM application id. Necessary to send RUM data to DD */
  applicationId?: string | null
  /** Percentage of traffic that should be recorded by Datadog RUM, where 0 is no users and 100 is all users */
  sampleRate: number
  /** Percentage of users that should have their sessions recorded by Datadog Session Replay */
  premiumSampleRate?: number
  /** Forward errors to logger */
  forwardErrorsToLogs?: boolean
  /** name of the service you will be monitoring. Example.: checkout, search, booking-management */
  service?: string
  /** URLs to trace for backend correlation */
  allowedTracingUrls?: TracingOption[]
  /** Brand code of the current white-label (e.g. 'vio', 'kiwi'). Attached as global context to RUM and Logs. */
  brandCode?: string
  /** SAPI profile key in use. Attached as global context to RUM and Logs. */
  profileKey?: string
  /** Datadog cost_center tag - used to attribute spend by technical domain. */
  costCenter?: string
  /**
   * Base URL of the first-party forwarder for Datadog intake, to survive ad
   * blockers. Either a same-origin path ('/insights') or an absolute
   * first-party URL. Omit to send straight to Datadog.
   */
  proxyUrl?: string
}

const COST_CENTER_TAG = 'website'
const DEFAULT_PRIVACY_LEVEL = 'mask-user-input'
export const DD_SITE = 'datadoghq.com'

/**
 * Datadog's string `proxy` form packs the intake path into a `ddforward` query
 * param, so the forwarder has to parse it, validate it and rebuild the URL. The
 * function form lets us keep the path in the path, which makes the forwarder a
 * plain reverse proxy with a fixed destination and nothing to validate.
 */
const buildProxyUrl =
  (proxyUrl: string) =>
  ({path, parameters}: {path: string; parameters: string}) =>
    `${proxyUrl}${path}?${parameters}`

/**
 * Fires an action to tracks that the user initiated a sign in process
 * @param event - The event name
 * @param metadata - Extra event data
 * @param level - Type of the event from StatusType
 */
export const dataDogLogEvent = (
  event: string,
  metadata: Record<string, unknown> | undefined,
  level: StatusType = StatusType.info
) => {
  datadogLogs.logger.log(event, metadata, level)
}

/**
 * Create a new logger based on a given name. Useful to compartmentalise logging into different scopes
 * @param loggerName the name of the new logger context you want to create
 * @returns a datadog logger
 */
export const createDataDogLogger = (loggerName: string) => {
  return datadogLogs.createLogger(loggerName)
}

const datadogRumMock: Pick<
  typeof datadogRum,
  'addTiming' | 'addAction' | 'addFeatureFlagEvaluation'
> = {
  addTiming(name, time) {
    return [name, time]
  },
  addAction(name, args) {
    return [name, args]
  },
  addFeatureFlagEvaluation(featureKey, featureEnabled) {
    return [featureKey, featureEnabled]
  }
}

export const getDataDogRumInstance = () => {
  return datadogRum || datadogRumMock
}

/**
 * Sets a global context property on the DataDog RUM client. Safe to call before
 * `datadogRum.init()` — the SDK buffers pre-init calls and applies them at init.
 */
export const setDataDogRumGlobalContextProperty = (
  key: string,
  value: ContextValue
) => {
  datadogRum.setGlobalContextProperty(key, value)
}

export const beforeSend = () => {
  const experiments = experimentVariationsStore.getAll(true)
  datadogRum.setGlobalContextProperty('experiments', experiments)
  return true
}

/**
 * Init Datadog client with the necessary token, app version and env
 */
export const InitializeDataDogClient = ({
  clientToken,
  appVersion,
  applicationId,
  sampleRate,
  premiumSampleRate,
  appEnv,
  forwardErrorsToLogs = false,
  service = undefined,
  allowedTracingUrls = undefined,
  brandCode = undefined,
  profileKey = undefined,
  costCenter = COST_CENTER_TAG,
  proxyUrl = undefined
}: InitializeDataDogClientParametersType) => {
  const proxy = proxyUrl ? buildProxyUrl(proxyUrl) : undefined

  if (clientToken && applicationId && sampleRate > 0) {
    runInIdleTime(() => {
      datadogRum.init({
        clientToken,
        applicationId,
        sessionSampleRate: sampleRate,
        sessionReplaySampleRate: premiumSampleRate,
        version: appVersion,
        env: appEnv,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: DEFAULT_PRIVACY_LEVEL,
        site: DD_SITE,
        proxy,
        service,
        beforeSend,
        enableExperimentalFeatures: ['feature_flags'],
        allowedTracingUrls
      })
      datadogRum.setGlobalContextProperty('cost_center', costCenter)

      mapObjIndexed<ContextValue, void, string>((value, key) => {
        if (value) {
          datadogRum.setGlobalContextProperty(key, value)
        }
      })({brandCode, profileKey})
    })
  }

  if (clientToken) {
    runInIdleTime(() => {
      datadogLogs.init({
        clientToken,
        site: DD_SITE,
        proxy,
        forwardErrorsToLogs,
        service
      })
      datadogLogs.logger.addTag('cost_center', costCenter)

      mapObjIndexed<ContextValue, void, string>((value, key) => {
        if (value) {
          datadogLogs.setGlobalContextProperty(key, value)
        }
      })({appVersion, env: appEnv, brandCode, profileKey})
    })
  }

  return null
}

export default React.memo(InitializeDataDogClient)
